import { useUserContext } from '@/contexts/UserContext.tsx'
import { getEntity, redefineTaskOrder } from '@/requests/apollo/cache.ts'
import { getMutation } from '@/requests/apollo/mutation.ts'
import { TASK_COMMAND_META, TASK_GQL_SCHEMA } from '@/requests/apollo/schema.ts'
import { getExecuteTemplate } from '@/requests/apollo/templates.ts'
import { useQueryProjectMeta } from '@/requests/queries/useQueryProject.ts'
import { getSpliceIndex } from '@/utils/array.ts'
import { isTemporaryId } from '@/utils/key.ts'
import { type Reference, useApolloClient } from '@apollo/client'

export const useMoveTask = () => {
  // biome-ignore lint/style/noNonNullAssertion: This is guaranteed to be non-null
  const user = useUserContext().user!
  const { projectMeta } = useQueryProjectMeta()
  const { cache } = useApolloClient()

  const [execute] = getMutation('Task', TASK_COMMAND_META)

  return (storyId: string, taskId: string, overTaskId: string) => {
    cache.modify({
      id: cache.identify({ __typename: 'Story', id: storyId }),
      fields: {
        // biome-ignore lint/style/useDefaultParameterLast: This is a GraphQL cache configuration
        tasks: (existingEntityRefs = [], { readField }) => {
          const taskIdList = (existingEntityRefs as Reference[]).map((ref) => readField('id', ref))
          const targetTaskIndex = taskIdList.findIndex((id) => id === taskId)
          const overTaskIndex = taskIdList.findIndex((id) => id === overTaskId)

          if (targetTaskIndex === -1 || overTaskIndex === -1) return

          const targetTask = readField('id', existingEntityRefs[targetTaskIndex])
          const updatedIdList = taskIdList.filter((id) => id !== taskId)

          const adjustedSpliceIndex = getSpliceIndex(overTaskIndex, [targetTaskIndex], targetTaskIndex)

          updatedIdList.splice(adjustedSpliceIndex, 0, targetTask)

          return updatedIdList.map((id) => existingEntityRefs.find((ref: Reference) => readField('id', ref) === id))
        },
      },
    })
    redefineTaskOrder(cache.identify({ __typename: 'Story', id: storyId }))

    if (isTemporaryId(storyId)) return Promise.resolve()

    const entity = getEntity<Task>('Task', TASK_GQL_SCHEMA, taskId)
    if (!entity) {
      return Promise.reject(new Error(`Task with id ${taskId} not found`))
    }

    return execute(getExecuteTemplate(projectMeta, 'TASK_MOVE', user.id, entity, [entity]))
  }
}
